import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Calling from '../../api/Calling';
import { TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import {
    Plus,
    Pencil,
    Trash2,
    Search,
    X,
    Check,
    ChevronUp,
    ChevronDown,
    Image as ImageIcon,
    Link2,
    BellRing,
    Type,
} from 'lucide-react';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';
import AutoNotNav from './AutoNotNav';
import TableSkeleton from '../../components/ul/TableSkeleton';
import ImageWithShimmer from '../../components/ul/ImageWithShimmer';
import ImageDropzone from '../../components/ul/ImageDropzone';

// Shared MUI light-field styling
const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' },
};

const AutoNotificationTemp = () => {
    // State management
    const [loader, setLoader] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // Sorting states
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Selection states for bulk delete
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    // Form states
    const [formData, setFormData] = useState({ title: '', message: '', url: '', image: null, n_status: true });
    const [editId, setEditId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Existing server image URL (shown while editing, until a new file is picked)
    const [editImagePreview, setEditImagePreview] = useState(null);

    // Fetch templates data
    const fetchTemplates = async () => {
        setLoader(true);
        try {
            const response = await Calling.getAutoNotTemplates();
            if (response?.status) {
                setTemplates(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to fetch templates');
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            toast.error('Failed to fetch templates');
        } finally {
            setLoader(false);
            setSelectedIds([]);
            setSelectAll(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Search and Sort filter
    useEffect(() => {
        let result = [...templates];

        if (searchQuery.trim() !== '') {
            result = result.filter(item =>
                item.n_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.n_message?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        result.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (sortField === 'n_title' || sortField === 'n_message') {
                aValue = (aValue || '').toLowerCase();
                bValue = (bValue || '').toLowerCase();
            }

            if (sortField === 'createdAt') {
                aValue = new Date(aValue || 0).getTime();
                bValue = new Date(bValue || 0).getTime();
            }

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredData(result);
        setSelectedIds([]);
        setSelectAll(false);
    }, [searchQuery, templates, sortField, sortOrder]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredData.map(item => item._id));
        }
        setSelectAll(!selectAll);
    };

    const handleSelectItem = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(itemId => itemId !== id));
            setSelectAll(false);
        } else {
            const newSelected = [...selectedIds, id];
            setSelectedIds(newSelected);
            if (newSelected.length === filteredData.length) {
                setSelectAll(true);
            }
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        } else if (formData.title.length < 2) {
            errors.title = 'Title must be at least 2 characters';
        }

        if (!formData.message.trim()) {
            errors.message = 'Message is required';
        } else if (formData.message.length < 5) {
            errors.message = 'Message must be at least 5 characters';
        }

        if (formData.url && !/^https?:\/\/.+/.test(formData.url)) {
            errors.url = 'URL must start with http:// or https://';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Validate + store a picked/dropped/pasted image (preview handled by ImageDropzone).
    // Passing null clears the current selection.
    const processTemplateImage = (file) => {
        if (!file) {
            setFormData(prev => ({ ...prev, image: null }));
            return;
        }
        if (!file.type.startsWith('image/')) {
            toast.error('Please select a valid image file');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should not exceed 5MB');
            return;
        }
        setFormData(prev => ({ ...prev, image: file }));
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title.trim());
            formDataToSend.append('message', formData.message.trim());
            formDataToSend.append('status', formData.n_status);
            if (formData.url.trim()) {
                formDataToSend.append('url', formData.url.trim());
            }
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            const response = await Calling.addAutoNotTemplate(formDataToSend);
            if (response?.status) {
                toast.success(response?.message || 'Template added successfully');
                setShowAddModal(false);
                resetForm();
                fetchTemplates();
            } else {
                toast.error(response?.message || 'Failed to add template');
            }
        } catch (error) {
            console.error('Error adding template:', error);
            toast.error('Failed to add template');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('id', editId);
            formDataToSend.append('title', formData.title.trim());
            formDataToSend.append('message', formData.message.trim());
            formDataToSend.append('status', formData.n_status);
            if (formData.url.trim()) {
                formDataToSend.append('url', formData.url.trim());
            }
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            const response = await Calling.updateAutoNotTemplate(formDataToSend);
            if (response?.status) {
                toast.success(response?.message || 'Template updated successfully');
                setShowEditModal(false);
                resetForm();
                fetchTemplates();
            } else {
                toast.error(response?.message || 'Failed to update template');
            }
        } catch (error) {
            console.error('Error updating template:', error);
            toast.error('Failed to update template');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (idsToDelete) => {
        const count = idsToDelete.length;
        const content = `You are about to delete ${count} template(s). To confirm, please solve this math problem`;
        const check = generateCaptcha(content);
        if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

        setLoader(true);
        try {
            const body = { id: idsToDelete };
            const response = await Calling.deleteAutoNotTemplate(body);

            if (response?.status) {
                toast.success(response?.message || `${count} template(s) deleted successfully`);
                fetchTemplates();
            } else {
                toast.error(response?.message || 'Failed to delete template(s)');
                setLoader(false);
            }
        } catch (error) {
            console.error('Error deleting template(s):', error);
            toast.error('Failed to delete template(s)');
            setLoader(false);
        }
    };

    const handleDeleteSingle = (id) => {
        handleDelete([id]);
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) {
            return toast.error('Please select at least one template to delete');
        }
        handleDelete(selectedIds);
    };

    const openEditModal = (item) => {
        setFormData({
            title: item.n_title || '',
            message: item.n_message || '',
            url: item.n_url || '',
            image: null,
            n_status: item.n_status !== undefined ? item.n_status : true
        });
        setEditId(item._id);
        setEditImagePreview(item.n_icon || null);
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({ title: '', message: '', url: '', image: null, n_status: true });
        setFormErrors({});
        setEditId(null);
        setEditImagePreview(null);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        resetForm();
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        resetForm();
    };

    // Shared template form (used by both Add & Edit modals)
    const renderForm = ({ onSubmit, isEdit, onCancel, submitLabel, submittingLabel }) => (
        <form onSubmit={onSubmit}>
            <div className="space-y-4">
                <FormInput
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter template title"
                    error={formErrors.title}
                    icon={Type}
                    required
                />

                <div>
                    <label className="block text-sm font-semibold text-main-font mb-2">
                        Message <span className="text-main-delete">*</span>
                    </label>
                    <TextField
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Enter template message"
                        multiline
                        minRows={3}
                        fullWidth
                        error={!!formErrors.message}
                        helperText={formErrors.message || ''}
                        sx={fieldSx}
                    />
                </div>

                <FormInput
                    label="URL"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    error={formErrors.url}
                    icon={Link2}
                    optional
                />

                <div>
                    <label className="block text-sm font-semibold text-main-font mb-2">
                        Image <span className="text-main-subfont font-normal">(optional)</span>
                    </label>
                    <ImageDropzone
                        file={formData.image}
                        onFile={processTemplateImage}
                        previewUrl={isEdit ? editImagePreview : null}
                        accept="image/png, image/jpg, image/jpeg, image/gif, image/webp"
                    />
                </div>

                <div className="bg-main-bg rounded-xl border border-main-border p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="text-main-font font-semibold block">Status</label>
                            <p className="text-sm text-main-subfont mt-0.5">Enable or disable this template</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={`text-sm font-semibold ${formData.n_status ? 'text-main-blue' : 'text-main-delete'}`}>
                                {formData.n_status ? 'On' : 'Off'}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, n_status: !prev.n_status }))}
                                className={`relative w-12 h-6 rounded-full transition-all ${formData.n_status ? 'bg-main-blue' : 'bg-main-border'}`}
                            >
                                <div className={`absolute top-1 w-4 h-4 bg-main-white rounded-full shadow-md transition-all ${formData.n_status ? 'left-7' : 'left-1'}`} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-main-border">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 px-4 py-3 border border-main-border text-main-font rounded-xl hover:bg-main-bg font-semibold transition-all"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-main-blue text-main-white rounded-xl hover:bg-[#2db84d] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-main-blue/25"
                >
                    {isSubmitting ? (
                        <>
                            <CircularProgress size={16} color="inherit" />
                            {submittingLabel}
                        </>
                    ) : (
                        <>
                            <Check size={18} />
                            {submitLabel}
                        </>
                    )}
                </button>
            </div>
        </form>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className='w-full min-h-screen bg-main-bg text-main-font rounded-2xl'
        >
            <div className="p-4">
                {/* Header */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6 mb-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-main-th rounded-xl flex items-center justify-center border border-main-blue/20">
                            <BellRing className="text-main-blue" size={22} />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-main-font">Auto Notification Manager</h1>
                            <p className="text-main-subfont text-sm mt-0.5">Manage notification templates</p>
                        </div>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-main-white w-full rounded-2xl border border-main-border shadow-sm overflow-hidden">
                    <AutoNotNav selectState={'template'} />

                    {/* Templates Header */}
                    <div className="flex items-center gap-3 p-5 md:p-6 border-b border-main-border bg-main-th/50">
                        <div className="w-10 h-10 bg-main-th rounded-lg flex items-center justify-center border border-main-blue/20">
                            <BellRing size={20} className="text-main-blue" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-main-font">Auto Notification Templates</h2>
                            <p className="text-sm text-main-subfont">Create and manage notification templates</p>
                        </div>
                    </div>

                    <div className='p-5 md:p-6'>
                        {/* Header Actions */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            {/* Search */}
                            <div className="w-full sm:w-80">
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search templates..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={fieldSx}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={18} className="text-main-subfont" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchQuery ? (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="clear search">
                                                        <X size={16} className="text-main-subfont" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 w-full sm:w-auto">
                                {selectedIds.length > 0 && (
                                    <motion.button
                                        whileTap={{ scale: 0.96 }}
                                        onClick={handleBulkDelete}
                                        className="flex items-center gap-2 px-5 py-3 bg-main-delete text-main-white rounded-xl hover:bg-main-delete/90 transition-all font-semibold text-sm shadow-md"
                                    >
                                        <Trash2 size={16} />
                                        Delete ({selectedIds.length})
                                    </motion.button>
                                )}
                                <motion.button
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => setShowAddModal(true)}
                                    className="flex items-center gap-2 px-5 py-3 bg-main-blue text-main-white rounded-xl hover:bg-[#2db84d] transition-all font-semibold text-sm shadow-md shadow-main-blue/25"
                                >
                                    <Plus size={16} />
                                    Add Template
                                </motion.button>
                            </div>
                        </div>

                        {/* Selection Info */}
                        {selectedIds.length > 0 && (
                            <div className="mb-4 px-4 py-3 bg-main-th rounded-xl text-sm text-main-blue flex items-center justify-between border border-main-blue/20">
                                <span className="font-medium">{selectedIds.length} of {filteredData.length} template(s) selected</span>
                                <button
                                    onClick={() => {
                                        setSelectedIds([]);
                                        setSelectAll(false);
                                    }}
                                    className="text-main-blue hover:text-[#2db84d] font-semibold transition-colors"
                                >
                                    Clear Selection
                                </button>
                            </div>
                        )}

                        {/* Templates Table */}
                        {loader ? (
                            <div className="border border-main-border rounded-xl overflow-hidden">
                                <TableSkeleton rows={6} cols={7} />
                            </div>
                        ) : (
                            <div className="overflow-x-auto border border-main-border rounded-xl">
                                <table className="w-full">
                                    <thead className="bg-main-th border-b border-main-border">
                                        <tr>
                                            <th className="px-4 py-4 text-left">
                                                <Checkbox
                                                    checked={selectAll}
                                                    indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th
                                                className="px-4 py-4 text-left cursor-pointer hover:bg-main-thHover transition-colors"
                                                onClick={() => handleSort('n_title')}
                                            >
                                                <div className="flex items-center gap-1 text-xs font-semibold text-main-font uppercase tracking-wider">
                                                    Title
                                                    {sortField === 'n_title' && (
                                                        sortOrder === 'asc' ? <ChevronUp size={14} className="text-main-blue" /> : <ChevronDown size={14} className="text-main-blue" />
                                                    )}
                                                </div>
                                            </th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Message</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Image</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">URL</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Status</th>
                                            <th
                                                className="px-4 py-4 text-left cursor-pointer hover:bg-main-thHover transition-colors"
                                                onClick={() => handleSort('createdAt')}
                                            >
                                                <div className="flex items-center gap-1 text-xs font-semibold text-main-font uppercase tracking-wider">
                                                    Created
                                                    {sortField === 'createdAt' && (
                                                        sortOrder === 'asc' ? <ChevronUp size={14} className="text-main-blue" /> : <ChevronDown size={14} className="text-main-blue" />
                                                    )}
                                                </div>
                                            </th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Updated</th>
                                            <th className="px-4 py-4 text-center text-xs font-semibold text-main-font uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-main-border">
                                        {filteredData.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" className="px-4 py-16 text-center">
                                                    <div className="flex flex-col items-center">
                                                        <div className="w-20 h-20 bg-main-th rounded-full flex items-center justify-center mb-4 border border-main-blue/20">
                                                            <BellRing className="text-main-blue" size={36} />
                                                        </div>
                                                        <p className="text-lg font-bold text-main-font">
                                                            {searchQuery ? 'No templates found' : 'No templates available'}
                                                        </p>
                                                        <p className="text-sm text-main-subfont mt-1">
                                                            {searchQuery ? 'Try a different search term' : 'Add your first template to get started'}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredData.map((template) => (
                                                <tr key={template._id} className={`hover:bg-main-bg transition-colors ${selectedIds.includes(template._id) ? 'bg-main-th' : ''}`}>
                                                    <td className="px-4 py-4">
                                                        <Checkbox
                                                            checked={selectedIds.includes(template._id)}
                                                            onChange={() => handleSelectItem(template._id)}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-4 font-semibold text-main-font">{template.n_title}</td>
                                                    <td className="px-4 py-4 max-w-xs truncate text-main-subfont">{template.n_message}</td>
                                                    <td className="px-4 py-4">
                                                        {template.n_icon ? (
                                                            <ImageWithShimmer
                                                                src={template.n_icon}
                                                                alt="Template"
                                                                className="w-10 h-10 rounded-lg border border-main-border"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 bg-main-bg rounded-lg flex items-center justify-center border border-main-border">
                                                                <ImageIcon className="text-main-subfont" size={20} />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        {template.n_url ? (
                                                            <a
                                                                href={template.n_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-main-exBlue hover:underline truncate block max-w-[150px] text-sm"
                                                            >
                                                                {template.n_url}
                                                            </a>
                                                        ) : (
                                                            <span className="text-main-subfont">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${template.n_status
                                                            ? 'bg-main-th text-main-blue border border-main-blue/20'
                                                            : 'bg-main-delete/10 text-main-delete border border-main-delete/20'
                                                            }`}>
                                                            {template.n_status ? 'On' : 'Off'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-main-subfont">
                                                        {formatDate(template.createdAt)}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-main-subfont">
                                                        {formatDate(template.updatedAt)}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => openEditModal(template)}
                                                                className="p-2.5 text-main-exBlue hover:bg-main-exBlue/10 rounded-xl transition-all border border-transparent hover:border-main-exBlue/20"
                                                                title="Edit"
                                                            >
                                                                <Pencil size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteSingle(template._id)}
                                                                className="p-2.5 text-main-delete hover:bg-main-delete/10 rounded-xl transition-all border border-transparent hover:border-main-delete/20"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Results Count */}
                        {!loader && filteredData.length > 0 && (
                            <div className="mt-5 text-sm text-main-subfont text-center">
                                Showing {filteredData.length} of {templates.length} templates
                            </div>
                        )}
                    </div>
                </div>

                {/* Add Modal */}
                <AnimatePresence>
                    {showAddModal && (
                        <Modal onClose={closeAddModal} title="Add Template">
                            {renderForm({
                                onSubmit: handleAdd,
                                isEdit: false,
                                onCancel: closeAddModal,
                                submitLabel: 'Add Template',
                                submittingLabel: 'Adding...',
                            })}
                        </Modal>
                    )}
                </AnimatePresence>

                {/* Edit Modal */}
                <AnimatePresence>
                    {showEditModal && (
                        <Modal onClose={closeEditModal} title="Edit Template">
                            {renderForm({
                                onSubmit: handleEdit,
                                isEdit: true,
                                onCancel: closeEditModal,
                                submitLabel: 'Update Template',
                                submittingLabel: 'Updating...',
                            })}
                        </Modal>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default AutoNotificationTemp;

// Modal Component
const Modal = ({ onClose, title, children }) => (
    <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
    >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
        <motion.div
            className="relative bg-main-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.95, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 12, opacity: 0 }}
            transition={{ duration: 0.2 }}
        >
            <div className="sticky top-0 bg-main-white flex items-center justify-between p-5 border-b border-main-border z-10">
                <h3 className="text-lg font-bold text-main-font">{title}</h3>
                <button
                    onClick={onClose}
                    className="w-9 h-9 flex items-center justify-center hover:bg-main-delete/10 rounded-xl transition-all text-main-subfont hover:text-main-delete"
                >
                    <X size={20} />
                </button>
            </div>
            <div className="p-5">{children}</div>
        </motion.div>
    </motion.div>
);

// Form Input Component (MUI)
const FormInput = ({ label, name, value, onChange, placeholder, error, icon: Icon, required, optional }) => (
    <div>
        <label className="block text-sm font-semibold text-main-font mb-2">
            {label} {required && <span className="text-main-delete">*</span>}
            {optional && <span className="text-main-subfont font-normal">(optional)</span>}
        </label>
        <TextField
            fullWidth
            size="small"
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            error={!!error}
            helperText={error || ''}
            sx={fieldSx}
            slotProps={{
                input: {
                    startAdornment: Icon ? (
                        <InputAdornment position="start">
                            <Icon size={18} className="text-main-subfont" />
                        </InputAdornment>
                    ) : undefined,
                },
            }}
        />
    </div>
);

// Checkbox Component
const Checkbox = ({ checked, onChange, indeterminate = false }) => (
    <div
        onClick={onChange}
        className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all ${checked || indeterminate
            ? 'bg-main-blue border-main-blue'
            : 'border-main-border hover:border-main-blue'
            }`}
    >
        {checked && <Check size={14} className="text-main-white" />}
        {indeterminate && !checked && (
            <div className="w-2.5 h-0.5 bg-main-white rounded"></div>
        )}
    </div>
);
